import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { resolveRecipients } from './resolveRecipients';
import TCClient from 'client';
import { actions, thunk, useAppSelector, ReduxState } from 'redux-stores';
import type { LaunchData } from 'stores/LaunchStore';

const { setSelectedPatientReference } = actions;
const { searchPatientContextsThunk } = thunk;

type Entity = { id: string };

export function useLaunchPCM(
  launchData: LaunchData,
  composeNewMessage: () => void,
  setSelectedRecipients: (entities: Entity[]) => void,
  currentUserId: string
) {
  const dispatch = useDispatch();
  const recipientsResolvedRef = useRef(false);
  const [resolvingRecipients, setResolvingRecipients] = useState(false);

  const { patientReferenceSearchList, patientReferenceSearchLoading, selectedPatientReference } =
    useAppSelector((state: ReduxState) => state.patientContext);
  const orgId = useAppSelector((state: ReduxState) => state.session.currentOrganizationId);

  useEffect(() => {
    if (orgId && launchData && launchData.type === 'pcm') {
      dispatch(searchPatientContextsThunk({ query: launchData.data.mrn, from: 0 }));
    }
  }, [orgId, launchData, dispatch]);

  useEffect(() => {
    if (
      !patientReferenceSearchLoading &&
      patientReferenceSearchList &&
      patientReferenceSearchList.length > 0
    ) {
      const [patient] = patientReferenceSearchList;
      dispatch(setSelectedPatientReference(patient));
    }
  }, [patientReferenceSearchList, patientReferenceSearchLoading, dispatch]);

  useEffect(() => {
    if (!selectedPatientReference) return;
    if (recipientsResolvedRef.current) return;
    recipientsResolvedRef.current = true;

    const recipients = launchData?.type === 'pcm' ? launchData.data.recipients : undefined;

    if (!recipients || !orgId) {
      composeNewMessage();
      return;
    }

    setResolvingRecipients(true);
    (async () => {
      try {
        const matches = await resolveRecipients({
          client: TCClient,
          organizationId: orgId,
          excludeIds: currentUserId ? [currentUserId] : [],
          recipients,
        });
        if (matches.length > 0) {
          setSelectedRecipients(matches);
        }
      } catch (err) {
        console.error(
          'Failed to resolve FHIR launch recipients; falling back to empty To-line',
          err
        );
      } finally {
        composeNewMessage();
        setResolvingRecipients(false);
      }
    })();
  }, [
    selectedPatientReference,
    launchData,
    orgId,
    currentUserId,
    composeNewMessage,
    setSelectedRecipients,
  ]);

  const hasSearchResults =
    Array.isArray(patientReferenceSearchList) && patientReferenceSearchList.length > 0;

  const isAwaitingSelection =
    launchData?.type === 'pcm' &&
    !selectedPatientReference &&
    patientReferenceSearchLoading === false &&
    (patientReferenceSearchList === undefined || hasSearchResults);

  const noResults =
    launchData?.type === 'pcm' &&
    patientReferenceSearchLoading === false &&
    Array.isArray(patientReferenceSearchList) &&
    patientReferenceSearchList.length === 0;

  return {
    loading: patientReferenceSearchLoading || isAwaitingSelection || resolvingRecipients,
    noResults,
  };
}
